import {
  JFormik,
  sortString,
  useBreakpointQuery,
} from '@centerpiece/components';
import {
  Badge,
  Box,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useProjectContext } from 'contexts/project.context';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  useCreateBillingMutation,
  useDeleteBillingMutation,
  useGetBillingsQuery,
  useGetBillingTypesQuery,
  useGetPaymentTypesQuery,
  useUpdateBillingMutation,
} from 'store/api';

import { Project } from 'store/api/models';
import {
  useGetPaymentsQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
} from 'store/api/payment.api';
import * as Yup from 'yup';

export interface ProjectBillingsTabProps {
  id: number;
  archived?: boolean;
  previewTab?: boolean;
  template?: boolean;
  exampleProject?: Project;
  afterSave?: Function;
}

export const ProjectBillingsTab = (props: ProjectBillingsTabProps) => {
  const [value, setValue] = useState<string>('billing');
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const mdDown = useBreakpointQuery('md-down');
  const smDown = useBreakpointQuery('sm-down');
  const [editing /*, setEditing*/] = useState<boolean>(false);
  const { id, project } = useProjectContext();
  const {
    data: billing_types = [],
    isLoading: billingTypesLoading,
    refetch: refetchBillingTypes,
  } = useGetBillingTypesQuery({});
  const {
    data: payment_types = [],
    isLoading: paymentTypesLoading,
    refetch: refetchPaymentTypes,
  } = useGetPaymentTypesQuery({});

  const { data: billings = [], isLoading } = useGetBillingsQuery({
    params: { project: id },
  });
  const [createBilling] = useCreateBillingMutation();
  const [editBilling] = useUpdateBillingMutation();
  const [deleteBilling] = useDeleteBillingMutation();

  const { data: payments = [], isLoading: isPaymentsLoading } =
    useGetPaymentsQuery({
      params: { project: id },
    });
  const [createPayment] = useCreatePaymentMutation();
  const [editPayment] = useUpdatePaymentMutation();
  const [deletePayment] = useDeletePaymentMutation();
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  const billingData = useMemo(() => {
    let btotal: number = 0;
    billings?.forEach((f: any) => {
      const amount = Number(f.amount || 0);
      btotal += amount;
    });
    let ptotal: number = 0;
    payments?.forEach((f: any) => {
      const amount = Number(f.amount || 0);
      ptotal += amount;
    });
    let formatted_btotal = '';
    if (btotal >= 1000000) {
      formatted_btotal = '$' + (btotal / 1000000).toFixed(1) + 'M';
    } else if (btotal >= 1000) {
      formatted_btotal = '$' + (btotal / 1000).toFixed(1) + 'K';
    } else {
      formatted_btotal = '$' + btotal;
    }
    let formatted_ptotal = '';
    if (ptotal >= 1000000) {
      formatted_ptotal = '$' + (ptotal / 1000000).toFixed(1) + 'M';
    } else if (ptotal >= 1000) {
      formatted_ptotal = '$' + (ptotal / 1000).toFixed(1) + 'K';
    } else {
      formatted_ptotal = '$' + ptotal;
    }
    const contractrevenue = Number(
      project?.form?.contractrevenue?.replaceAll(',', '')
    );
    const bidrevenue = Number(project?.form?.bidrevenue?.replaceAll(',', ''));
    const budgetrevenue = Number(project?.form?.revenue?.replaceAll(',', ''));
    const [projectrevenue, revenue_type] = [
      contractrevenue,
      'Contract Revenue',
    ];
    // const [projectrevenue, revenue_type] =
    //   contractrevenue > 0
    //     ? [contractrevenue, 'Contract Revenue']
    // : bidrevenue > 0
    // ? [bidrevenue, 'Bid/Quote Revenue']
    // : budgetrevenue > 0
    // ? [budgetrevenue, 'Budget Revenue']
    // : [0, undefined];

    let formatted_project_revenue = '';
    if (projectrevenue >= 1000000) {
      formatted_project_revenue =
        '$' + (projectrevenue / 1000000).toFixed(1) + 'M';
    } else if (projectrevenue >= 1000) {
      formatted_project_revenue =
        '$' + (projectrevenue / 1000).toFixed(1) + 'K';
    } else {
      formatted_project_revenue = '$' + projectrevenue;
    }
    return {
      labels: ['Contract Revenue', 'Total Billings', 'Total Payments'],
      datasets: [
        {
          label: '',
          data: [projectrevenue, btotal, ptotal],
          backgroundColor: ['#163B3F', 'gray', '#76B041'],
        },
      ],
      formatted_btotal,
      formatted_ptotal,
      formatted_project_revenue,
      projectrevenue,
      revenue_type,
      btotal,
      ptotal,
    };
  }, [
    billings,
    payments,
    project?.form?.contractrevenue,
    project?.form?.bidrevenue,
    project?.form?.revenue,
  ]);

  return (
    <Container maxWidth={'xl'}>
      <Paper elevation={5} sx={{ height: '100%', p: 1 }}>
        <Grid
          container
          alignItems="center"
          justifyContent={'space-between'}
          sx={{
            py: 1,
            px: 3,
          }}
        >
          <Grid item>
            <Typography variant="h6">Billing and Payment Summary</Typography>
          </Grid>
        </Grid>
        <Grid container alignItems="center">
          <Grid xs={12} md={2}>
            <Box sx={{ pl: 3 }}>
              <Typography variant="caption">
                <b>Contract Revenue:</b> {billingData.formatted_project_revenue}
              </Typography>
            </Box>
            <Box sx={{ pl: 3 }}>
              <Typography variant="caption">
                <b>Total Billing:</b> {billingData.formatted_btotal}
              </Typography>
            </Box>
            <Box sx={{ pl: 3 }}>
              <Typography variant="caption">
                <b>Total Payments:</b> {billingData.formatted_ptotal}
              </Typography>
            </Box>

            <Box sx={{ pl: 3 }}>
              <Typography variant="caption">
                <b>% Billed:</b>{' '}
                {billingData.projectrevenue > 0
                  ? `${(
                      (100 * Number(billingData.btotal)) /
                      billingData.projectrevenue
                    ).toFixed(1)}%`
                  : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ pl: 3 }}>
              <Typography variant="caption">
                <b>% Paid:</b>{' '}
                {billingData.projectrevenue > 0
                  ? `${(
                      (100 * Number(billingData.ptotal)) /
                      billingData.projectrevenue
                    ).toFixed(1)}%`
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid xs={12} md={10}>
            <Box
              sx={{
                // pb: 1,
                pl: { xs: 1, md: 3 },
                pr: { xs: 2, md: 4 },
                height: '80%',
              }}
            >
              <Bar
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: {
                    padding: {
                      top: 25,
                      right: 75,
                    },
                  },
                  plugins: {
                    legend: {
                      display: false,
                      position: 'bottom' as const,
                    },
                    title: {
                      display: false,
                      // text: 'Chart.js Bar Chart',
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          let value = Number(context.raw);
                          if (value >= 1000000) {
                            return (
                              context?.dataset?.label +
                              ': $' +
                              (value / 1000000).toFixed(1) +
                              'M'
                            );
                          } else if (value >= 1000) {
                            return (
                              context?.dataset?.label +
                              ': $' +
                              (value / 1000).toFixed(1) +
                              'K'
                            );
                          } else {
                            return context?.dataset?.label + ': $' + value;
                          }
                        },
                      },
                    },
                    datalabels: {
                      color: 'white', // Color of the data labels
                      display: true, // Set this to true to display data labels
                      align: 'start',
                      anchor: 'end',
                      font: {
                        weight: 'bold',
                      },
                      formatter: function (value, context) {
                        if (value >= 1000000) {
                          return '$' + (value / 1000000).toFixed(1) + 'M';
                        } else if (value >= 1000) {
                          return '$' + (value / 1000).toFixed(1) + 'K';
                        } else if (value > 0) {
                          return '$' + value;
                        } else {
                          return '';
                        }
                      },
                      labels: {
                        total: {
                          color: '#333333', // Color of the data labels
                          display: true, // Set this to true to display data labels
                          align: 'end',
                          offset: 25,
                          anchor: 'end',
                          font: {
                            weight: 'bold',
                          },
                          formatter: function (val, context) {
                            const value = (context?.dataset as any)?.total;
                            if (value >= 1000000) {
                              return '$' + (value / 1000000).toFixed(1) + 'M';
                            } else if (value >= 1000) {
                              return '$' + (value / 1000).toFixed(1) + 'K';
                            } else if (value > 0) {
                              return '$' + value;
                            } else {
                              return '';
                            }
                          },
                        },
                        // total2: {
                        //   color: 'white', // Color of the data labels
                        //   display: true, // Set this to true to display data labels
                        //   align: 'start',
                        //   // offset: 25,
                        //   anchor: 'end',
                        //   font: {
                        //     weight: 'bold',
                        //   },
                        //   formatter: function (value, context) {
                        //     if (value >= 1000000) {
                        //       return '$' + (value / 1000000).toFixed(1) + 'M';
                        //     } else if (value >= 1000) {
                        //       return '$' + (value / 1000).toFixed(1) + 'K';
                        //     } else if (value > 0) {
                        //       return '$' + value;
                        //     } else {
                        //       return '';
                        //     }
                        //   },
                        // },
                      },
                    },
                  },
                  scales: {
                    x: {
                      // stacked: true, // Enable stacking for the X-axis
                      ticks: {
                        // Custom callback function to format Y-axis labels with dollar signs
                        callback: function (value, index, values) {
                          const nValue = Number(value);
                          if (nValue >= 1000000) {
                            return '$' + (nValue / 1000000).toFixed(1) + 'M';
                          } else if (nValue >= 1000) {
                            return '$' + (nValue / 1000).toFixed(1) + 'K';
                          } else {
                            return '$' + nValue;
                          }
                        },
                      },
                    },
                    // y: {
                    //   stacked: true,
                    // },
                  },
                }}
                data={billingData}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          flexDirection={smDown ? 'column' : 'row'}
          alignItems={smDown ? 'flex-start' : 'center'}
          justifyContent="space-between"
          sx={{
            pl: { xs: 6, md: 8 },
            pr: { xs: 3, md: 5 },
            pt: 2,
            // height: '10%',
          }}
        ></Grid>
      </Paper>
      <Paper sx={{ px: 3, pb: 3, pt: 1, mt: 2 }} elevation={5}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            variant={'scrollable'}
            onChange={(event, value) => handleChange(event, value)}
            sx={{
              width: '100%',
              '& .MuiTabs-flexContainer': {
                pl: 0,
              },
            }}
          >
            <Tab key={'billing'} label={'Billings'} value={'billing'} />
            <Tab key={'payment'} label={'Payments'} value={'payment'} />
          </Tabs>
        </Box>
        {value === 'billing' && (
          <JFormik
            initialValues={{
              billings: billings.map(b => ({
                ...b,
                type: billing_types.find(bt => bt.id === b.type),
              })),
            }}
            handleSubmit={async (values, formik) => {}}
            loading={isLoading}
            validationSchema={
              editing
                ? Yup.object({
                    type: Yup.string().required('Billing type is required'),
                    amount: Yup.number()
                      .nullable()
                      .required('Billing amount is required'),
                  })
                : Yup.object({})
            }
            disabled={!editing}
            form={[
              {
                name: 'billings',
                itemType: 'table_array',
                label: 'Billings',
                jItemProps: {
                  addLabel: 'Add Billing',
                },
                tableProps: {
                  onCreate: (values: any) =>
                    createBilling({
                      ...values,
                      amount: values?.amount
                        ? Number(values?.amount?.replaceAll(',', ''))
                        : undefined,
                      type: values?.type?.id,
                      project: id,
                    }),
                  onEdit: (values: any) =>
                    editBilling({
                      ...values,
                      amount: values?.amount
                        ? Number(values?.amount?.replaceAll(',', ''))
                        : undefined,
                      type: values?.type?.id,
                      project: id,
                    }),
                  onDelete: (id: number) => deleteBilling(id),
                  headers: [
                    {
                      name: 'type',
                      label: 'Type *',
                      itemType: 'autocomplete',
                      options: billing_types,
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'amount',
                      label: 'Amount *',
                      itemType: 'textfield',
                      jItemProps: {
                        type: 'formatted_currency',
                        // type: 'number',
                        InputProps: {
                          startAdornment: '$',
                        },
                        inputProps: {
                          inputMode: 'numeric',
                        },
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'date',
                      label: 'Date',
                      itemType: 'datepicker',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'form.notes',
                      label: 'Notes',
                      itemType: 'textfield',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                  ],
                  columns: [
                    {
                      name: 'type',
                      label: 'Type *',
                      itemType: 'autocomplete',
                      options: billing_types,
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'amount',
                      label: 'Amount *',
                      itemType: 'textfield',
                      jItemProps: {
                        type: 'formatted_currency',
                        // type: 'number',
                        InputProps: {
                          startAdornment: '$',
                        },
                        inputProps: {
                          inputMode: 'numeric',
                        },
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'date',
                      label: 'Date',
                      itemType: 'datepicker',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'form.notes',
                      label: 'Notes',
                      itemType: 'textfield',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                  ],
                },
                device: downSm ? 'mobile' : 'desktop',
              },
            ]}
          />
        )}
        {value === 'payment' && (
          <JFormik
            initialValues={{
              payments: payments.map(p => ({
                ...p,
                type: payment_types.find(pt => pt.id === p.type),
              })),
            }}
            handleSubmit={async (values, formik) => {}}
            loading={isPaymentsLoading}
            validationSchema={
              editing
                ? Yup.object({
                    type: Yup.string().required('Payment type is required'),
                    amount: Yup.number()
                      .nullable()
                      .required('Payment amount is required'),
                  })
                : Yup.object({})
            }
            disabled={!editing}
            form={[
              {
                name: 'payments',
                itemType: 'table_array',
                label: 'Payments',
                jItemProps: {
                  addLabel: 'Add Payment',
                },
                tableProps: {
                  onCreate: (values: any) =>
                    createPayment({
                      ...values,
                      amount: values?.amount
                        ? Number(values?.amount?.replaceAll(',', ''))
                        : undefined,
                      type: values?.type?.id,
                      project: id,
                    }),
                  onEdit: (values: any) =>
                    editPayment({
                      ...values,
                      amount: values?.amount
                        ? Number(values?.amount?.replaceAll(',', ''))
                        : undefined,
                      type: values?.type?.id,
                      project: id,
                    }),
                  onDelete: (id: number) => deletePayment(id),
                  headers: [
                    {
                      name: 'type',
                      label: 'Type *',
                      itemType: 'autocomplete',
                      options: payment_types,
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'amount',
                      label: 'Amount *',
                      itemType: 'textfield',
                      jItemProps: {
                        type: 'formatted_currency',
                        // type: 'number',
                        InputProps: {
                          startAdornment: '$',
                        },
                        inputProps: {
                          inputMode: 'numeric',
                        },
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'date',
                      label: 'Date',
                      itemType: 'datepicker',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'form.notes',
                      label: 'Notes',
                      itemType: 'textfield',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                  ],
                  columns: [
                    {
                      name: 'type',
                      label: 'Type *',
                      itemType: 'autocomplete',
                      options: payment_types,
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'amount',
                      label: 'Amount *',
                      itemType: 'textfield',
                      jItemProps: {
                        type: 'formatted_currency',
                        // type: 'number',
                        InputProps: {
                          startAdornment: '$',
                        },
                        inputProps: {
                          inputMode: 'numeric',
                        },
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'date',
                      label: 'Date',
                      itemType: 'datepicker',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                    {
                      name: 'form.notes',
                      label: 'Notes',
                      itemType: 'textfield',
                      jItemProps: {
                        width: '25%',
                        sx: { boxSizing: 'border-box', px: { xs: 0.5 } },
                      },
                    },
                  ],
                },
                device: downSm ? 'mobile' : 'desktop',
              },
            ]}
          />
        )}
      </Paper>
    </Container>
  );
};
